import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';

import { SearchQuery, SearchResultEntity, SharedWithGroup } from '../store/search.models';
import {
  IChartConfig,
  IColumn,
  IColumnOrder,
  IConvertToExtendedProps,
  IConvertToExtendedResult,
  IExports,
  IFetchNavigationListProps,
  IFetchNewPageProps,
  IFetchSearchResultProps,
  IFilter,
  ILiveSearch,
  ISavedSearchQuerySummary
} from '../store/search.types';

import { environment } from '~/core/core.module';
import { SEARCHTYPE } from '~/search/search/search.const';
import { mapSelectedFilters } from '~/search/search/search.helpers';
import { IExportResultsProps, IFilterItem, PaginatedItemsParams } from '~/search/search/search.types';

@Injectable()
export class SearchService {
  constructor(private readonly http: HttpClient) {}

  public getSearchResults({
    type,
    filterData,
    columns,
    params,
    view,
    viewModeParams,
    userPreferences
  }: IFetchSearchResultProps): Observable<SearchResultEntity[]> {
    if (type === SEARCHTYPE.SIMPLE) {
      return this.http.post<SearchResultEntity[]>(`${environment.BACKEND_URL}/search/${type}`, {
        filters: mapSelectedFilters(type, filterData),
        columns: columns.map((col) => col.code),
        sortColumn: params.sortColumn || undefined,
        sortOrder: params.sortOrder || undefined,
        page: params.pageNumber,
        pageSize: params.currentItems,
        view,
        ...viewModeParams,
        ...userPreferences
      });
    }

    const parentOperator = filterData[0]?.selectedFilterBehaviour?.value || 'AND';

    return this.http.post<SearchResultEntity[]>(`${environment.BACKEND_URL}/search/${type}`, {
      filterGroup: {
        operator: parentOperator,
        ...mapSelectedFilters(type, filterData, parentOperator)
      },
      columns: columns.map((col) => col.code),
      sortColumn: params.sortColumn,
      sortOrder: params.sortOrder,
      page: params.pageNumber,
      pageSize: params.currentItems,
      view,
      ...viewModeParams,
      ...userPreferences
    });
  }

  public getNewPage({
    filterData,
    columns,
    type,
    params,
    module,
    view,
    viewModeParams,
    userPreferences
  }: IFetchNewPageProps): Observable<SearchResultEntity[]> {
    if (type === SEARCHTYPE.SIMPLE) {
      return this.http.post<SearchResultEntity[]>(`${environment.BACKEND_URL}/search/${type}`, {
        filters: mapSelectedFilters(type, filterData),
        columns: columns.map((col) => col.code),
        sortColumn: params.sortColumn,
        sortOrder: params.sortOrder,
        page: params.pageNumber,
        pageSize: params.currentItems,
        module,
        view,
        ...viewModeParams,
        ...userPreferences
      });
    }

    const parentOperator = filterData[0]?.selectedFilterBehaviour?.value || 'AND';

    return this.http.post<SearchResultEntity[]>(`${environment.BACKEND_URL}/search/${type}`, {
      filterGroup: {
        operator: parentOperator,
        ...mapSelectedFilters(type, filterData, parentOperator)
      },
      columns: columns.map((col) => col.code),
      module,
      page: params.pageNumber,
      pageSize: params.currentItems,
      sortColumn: params.sortColumn,
      sortOrder: params.sortOrder,
      view,
      ...viewModeParams,
      ...userPreferences
    });
  }

  public getNavigationList({
    filterData,
    type,
    module,
    params,
    viewModeParams,
    userPreferences
  }: IFetchNavigationListProps): Observable<SearchResultEntity[]> {
    if (type === SEARCHTYPE.SIMPLE) {
      return this.http.post<SearchResultEntity[]>(`${environment.BACKEND_URL}/search/${type}`, {
        filters: mapSelectedFilters(type, filterData),
        sortColumn: params.sortColumn,
        sortOrder: params.sortOrder,
        page: params.pageNumber,
        pageSize: params.currentItems,
        module: module,
        view: 'NAVIGATOR',
        ...viewModeParams,
        ...userPreferences
      });
    }

    const parentOperator = filterData[0]?.selectedFilterBehaviour?.value || 'AND';

    return this.http.post<SearchResultEntity[]>(`${environment.BACKEND_URL}/search/${type}`, {
      filterGroup: {
        operator: parentOperator,
        ...mapSelectedFilters(type, filterData, parentOperator)
      },
      module: module,
      page: params.pageNumber,
      pageSize: params.currentItems,
      sortColumn: params.sortColumn,
      sortOrder: params.sortOrder,
      view: 'NAVIGATOR',
      ...viewModeParams,
      ...userPreferences
    });
  }

  public getLiveSearch(query: string, kccFilter: string): Observable<ILiveSearch> {
    const params: { q: string; kccSearchFilter?: string } = {
      q: query
    };

    if (kccFilter) {
      params.kccSearchFilter = kccFilter;
    }

    return this.http.get<ILiveSearch>(`${environment.BACKEND_URL}/search/live`, {
      params: params
    });
  }

  public getFilters(): Observable<IFilter[]> {
    return this.http.get<IFilter[]>(`${environment.BACKEND_URL}/search/metadata/filters`);
  }

  public getColumns(): Observable<IColumn[]> {
    return this.http.get<IColumn[]>(`${environment.BACKEND_URL}/search/metadata/columns`);
  }

  public getSelectedColumns(): Observable<IColumn[]> {
    return this.http.get<IColumn[]>(`${environment.BACKEND_URL}/search/metadata/selectedcolumns`);
  }

  public getAvailableExports(): Observable<IExports> {
    return this.http.get<IExports>(`${environment.BACKEND_URL}/search/metadata/exports`);
  }

  public updateColumnsOrder(columnsOrder: IColumnOrder[]): Observable<IColumn[]> {
    return this.http.patch<IColumn[]>(`${environment.BACKEND_URL}/search/metadata/selectedcolumns/order`, columnsOrder);
  }

  public getChartConfig(): Observable<IChartConfig> {
    return this.http.get<IChartConfig>(`${environment.BACKEND_URL}/search/metadata/chart-options`);
  }

  public getMapConfig(): Observable<IColumn[]> {
    return this.http.get<IColumn[]>(`${environment.BACKEND_URL}/search/metadata/coordinates-columns`);
  }

  public saveQuery(
    id: string,
    name: string,
    filters: IFilterItem[],
    columns: IColumn[],
    params: PaginatedItemsParams,
    type: string,
    selectedViewMode: string,
    module: string,
    viewModeParams?: {
      [key: string]: unknown;
    }
  ): Observable<ISavedSearchQuerySummary> {
    let body;
    if (type === SEARCHTYPE.SIMPLE) {
      body = {
        name,
        filters: mapSelectedFilters(type, filters),
        columns: columns.map((col) => col.code),
        sortColumn: params.sortColumn,
        sortOrder: params.sortOrder,
        view: selectedViewMode,
        module,
        ...viewModeParams
      };
    } else {
      const parentOperator = filters[0]?.selectedFilterBehaviour?.value || 'AND';

      body = {
        name,
        filterGroup: {
          operator: parentOperator,
          ...mapSelectedFilters(type, filters, parentOperator)
        },
        columns: columns.map((col) => col.code),
        sortColumn: params.sortColumn,
        sortOrder: params.sortOrder,
        view: selectedViewMode,
        module,
        ...viewModeParams
      };
    }

    if (id) {
      return this.http.post<ISavedSearchQuerySummary>(
        `${environment.BACKEND_URL}/search/savedquery/${type}/${id}`,
        body
      );
    }
    return this.http.post<ISavedSearchQuerySummary>(`${environment.BACKEND_URL}/search/savedquery/${type}`, body);
  }

  public fetchQueries(): Observable<ISavedSearchQuerySummary[]> {
    return this.http.get<ISavedSearchQuerySummary[]>(`${environment.BACKEND_URL}/search/savedquery`);
  }

  public fetchSavedQuery(query: ISavedSearchQuerySummary): Observable<SearchQuery> {
    return this.http.get<SearchQuery>(
      `${environment.BACKEND_URL}/search/savedquery/${query.extended ? SEARCHTYPE.EXTENDED : SEARCHTYPE.SIMPLE}/${
        query.id
      }`
    );
  }

  public removeQuery(id: string): Observable<void> {
    return this.http.delete<void>(`${environment.BACKEND_URL}/search/savedquery/${id}`);
  }

  public getShareableGroups(): Observable<SharedWithGroup[]> {
    return this.http.get<SharedWithGroup[]>(`${environment.BACKEND_URL}/search/savedquery/shareable-groups`);
  }

  public getQuerySharedWith(queryId: string): Observable<SharedWithGroup[]> {
    return this.http.get<SharedWithGroup[]>(`${environment.BACKEND_URL}/search/savedquery/${queryId}/permissions`);
  }

  public updateQuerySharedWith(groups: SharedWithGroup[], queryId: string): Observable<void> {
    return this.http.post<void>(`${environment.BACKEND_URL}/search/savedquery/${queryId}/permissions`, groups);
  }

  public exportResults({
    filterData,
    columns,
    activeFilterTab,
    exportCode,
    module
  }: IExportResultsProps): Observable<number> {
    if (activeFilterTab === SEARCHTYPE.SIMPLE) {
      return this.http.post<number>(
        `${environment.BACKEND_URL}/search/${activeFilterTab}/export`,
        {
          filters: mapSelectedFilters(activeFilterTab, filterData),
          columns: columns.map((col) => col.code),
          module
        },
        {
          params: {
            code: exportCode
          }
        }
      );
    }

    const parentOperator = filterData[0]?.selectedFilterBehaviour?.value || 'AND';

    return this.http.post<number>(
      `${environment.BACKEND_URL}/search/${activeFilterTab}/export`,
      {
        filterGroup: {
          operator: parentOperator,
          ...mapSelectedFilters(activeFilterTab, filterData, parentOperator)
        },
        columns: columns.map((col) => col.code),
        module
      },
      {
        params: {
          code: exportCode
        }
      }
    );
  }

  public convertFiltersToExtended({
    filterData,
    userPreferences,
    activeResultsModule
  }: IConvertToExtendedProps): Observable<IConvertToExtendedResult> {
    return this.http.post<IConvertToExtendedResult>(`${environment.BACKEND_URL}/search/simple/convert-to-extended`, {
      filters: mapSelectedFilters(SEARCHTYPE.SIMPLE, filterData),
      ...userPreferences,
      module: activeResultsModule
    });
  }
}
